import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  Space,
  message,
  Row,
  Col,
  Typography,
  Alert,
  Breadcrumb,
  Divider,
  Spin
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import EmployeeSelect from '../components/common/EmployeeSelect';
import MessagePreview from '../components/sop/MessagePreview';
import AttachmentManager from '../components/sop/AttachmentManager';
import { useFixedFooter, useFixedFooterPlaceholder } from '../hooks/useFixedFooter';
import weworkContactWayService from '../services/weworkContactWayService';
import weworkService from '../services/weworkService';
import TagSelect from '../components/common/TagSelect';
import fileService from '../services/fileService';
import './WeworkContactWay.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
 

const WeworkContactWayEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [employees, setEmployees] = useState([]);
  const [previewStep, setPreviewStep] = useState(null);
  const [contactWay, setContactWay] = useState(null);
  const isEditMode = !!id;

  // 固定底部栏样式
  const fixedFooterStyle = useFixedFooter();
  const placeholderStyle = useFixedFooterPlaceholder();

  // 附件管理相关状态
  const [attachments, setAttachments] = useState([
    { id: 'text_1', type: 'TEXT', content: '' }
  ]);
  const [previewUrls, setPreviewUrls] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [nextAttachmentId, setNextAttachmentId] = useState(2);

  // 加载数据
  useEffect(() => {
    loadInitialData();
  }, [id]);

  // 加载初始数据
  const loadInitialData = async () => {
    setLoading(true);
    try {
      // 加载员工数据
      const employeesData = await weworkService.getAllEmployees().catch(err => {
        console.error('获取员工数据失败:', err);
        return [];
      });

      // 处理员工数据，确保正确格式
      let processedEmployees = [];
      if (Array.isArray(employeesData)) {
        processedEmployees = employeesData;
      } else if (employeesData && employeesData.content) {
        processedEmployees = employeesData.content;
      } else if (employeesData && Array.isArray(employeesData.data)) {
        processedEmployees = employeesData.data;
      }

      setEmployees(processedEmployees || []);

      // 编辑模式下加载活码详情
      if (isEditMode) {
        await loadContactWayDetail(processedEmployees || []);
      }
    } catch (error) {
      console.error('加载初始数据失败:', error);
      message.error('加载初始数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载活码详情
  const loadContactWayDetail = async (employeesData = employees) => {
    if (!id) {
      return;
    }

    try {
      console.log('🚀 开始加载活码详情，ID:', id);
      const data = await weworkContactWayService.getContactWayDetailById(id);
      console.log('📦 获取到的活码详情数据:', data);

      if (data.success) {
        const contactWayData = data.data;
        console.log('✅ 活码详情加载成功:', contactWayData);
        setContactWay(contactWayData);

        // 设置表单值
        const rawUserIds = contactWayData.user_ids || contactWayData.userIds || [];

        // 将企微userid转换为系统员工ID，供EmployeeSelect使用
        const convertedUserIds = rawUserIds.map(userId => {
          // 查找对应的员工记录
          const employee = employeesData.find(emp => emp.userid === userId || emp.userid === String(userId));
          if (employee) {
            return String(employee.id); // 返回系统员工ID
          } else {
            // 如果没找到对应员工，可能是系统ID格式，直接返回
            console.warn('未找到企微userid对应的员工:', userId);
            return String(userId);
          }
        });

        const formValues = {
          remark: contactWayData.remark || '',
          userIds: convertedUserIds, // 使用转换后的系统员工ID
          skipVerify: contactWayData.skip_verify !== undefined ? contactWayData.skip_verify :
            (contactWayData.skipVerify !== undefined ? contactWayData.skipVerify : false),
          autoTags: (contactWayData.auto_tag_ids || contactWayData.autoTagIds || []).map(id => (typeof id === 'number' ? id : parseInt(id, 10))).filter(v => !Number.isNaN(v))
        };

        console.log('📝 设置表单值:', formValues);
        console.log('📋 原始数据结构:', {
          user_ids: contactWayData.user_ids,
          userIds: contactWayData.userIds,
          skip_verify: contactWayData.skip_verify,
          skipVerify: contactWayData.skipVerify,
          auto_tag_ids: contactWayData.auto_tag_ids,
          autoTagIds: contactWayData.autoTagIds,
          welcome_messages: contactWayData.welcome_messages,
          welcomeMessages: contactWayData.welcomeMessages
        });
        form.setFieldsValue(formValues);

        // 初始化附件数据
        initializeAttachments(contactWayData);

      } else {
        console.error('❌ 获取活码详情失败:', data.message);
        message.error(data.message || '获取活码详情失败');
      }
    } catch (error) {
      console.error('❌ 加载活码详情异常:', {
        错误对象: error,
        错误消息: error.message,
        响应状态: error.response?.status,
        响应数据: error.response?.data,
        请求配置: error.config
      });
      message.error('获取活码详情失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 初始化附件数据的辅助方法
  const initializeAttachments = (contactWayData) => {
    // 优先使用下划线格式，fallback到驼峰格式
    const welcomeMessages = contactWayData.welcome_messages || contactWayData.welcomeMessages;

    if (welcomeMessages && welcomeMessages.length > 0) {
      // 如果有欢迎语，使用第一个作为数据源
      const welcomeMessage = welcomeMessages[0];
      console.log('📝 处理欢迎语数据:', welcomeMessage);

      const newAttachments = [
        { id: 'text_1', type: 'TEXT', content: welcomeMessage.textContent || welcomeMessage.text_content || '' }
      ];

      let attachmentIdCounter = 2;

      // 处理附件
      const attachmentData = welcomeMessage.attachments;
      if (attachmentData) {
        try {
          const attachmentsList = Array.isArray(attachmentData)
            ? attachmentData
            : JSON.parse(attachmentData);

          console.log('📎 解析到的附件列表:', attachmentsList);

          attachmentsList.forEach(att => {
            const attachmentId = `attachment_${attachmentIdCounter++}`;

            if (att.type === 'IMAGE_GROUP') {
              newAttachments.push({
                id: attachmentId,
                type: 'IMAGE_GROUP',
                imageGroup: att.imageGroup || { mediaIds: [], ossUrls: {} }
              });

              if (att.imageGroup && att.imageGroup.ossUrls) {
                setPreviewUrls(prev => ({ ...prev, ...att.imageGroup.ossUrls }));
              }
            } else if (att.type === 'VIDEO') {
              newAttachments.push({
                id: attachmentId,
                type: 'VIDEO',
                mediaId: att.mediaId || '',
                ossUrl: att.ossUrl || ''
              });

              if (att.mediaId && att.ossUrl) {
                setPreviewUrls(prev => ({ ...prev, [att.mediaId]: att.ossUrl }));
              }
            } else if (att.type === 'LINK') {
              newAttachments.push({
                id: attachmentId,
                type: 'LINK',
                title: att.title || '',
                description: att.description || '',
                url: att.url || ''
              });
            } else if (att.type === 'MINIPROGRAM') {
              newAttachments.push({
                id: attachmentId,
                type: 'MINIPROGRAM',
                title: att.title || '',
                appid: att.appid || '',
                pagepath: att.pagepath || '',
                thumbMediaId: att.thumbMediaId || '',
                thumbMediaId_ossUrl: att.thumbMediaId_ossUrl || ''
              });

              if (att.thumbMediaId && att.thumbMediaId_ossUrl) {
                setPreviewUrls(prev => ({ ...prev, [att.thumbMediaId]: att.thumbMediaId_ossUrl }));
              }
            } else if (att.type === 'FILE') {
              newAttachments.push({
                id: attachmentId,
                type: 'FILE',
                mediaId: att.mediaId || '',
                ossUrl: att.ossUrl || ''
              });

              if (att.mediaId && att.ossUrl) {
                setPreviewUrls(prev => ({ ...prev, [att.mediaId]: att.ossUrl }));
              }
            }
          });
        } catch (e) {
          console.error('❌ 解析附件数据失败:', e);
        }
      }

      console.log('📎 最终附件列表:', newAttachments);
      setAttachments(newAttachments);
      setNextAttachmentId(attachmentIdCounter);

      // 设置初始预览
      const textContent = welcomeMessage.textContent || welcomeMessage.text_content || '';
      updatePreviewFromData(textContent, newAttachments);
    } else {
      console.log('📋 没有欢迎语数据，使用默认附件');
    }
  };

  // 从数据更新预览的辅助方法
  const updatePreviewFromData = (textContent, attachmentsList) => {
    const initialAttachmentList = [];

    if (textContent) {
      initialAttachmentList.push({
        msgtype: 'text',
        text: { content: textContent }
      });
    }

    // 处理附件预览...
    // 这里可以根据需要添加附件预览逻辑

    setPreviewStep({
      type: 'MESSAGE',
      attachments: initialAttachmentList,
      delay: 0,
      nextStepOptions: []
    });
  };

  // 处理文件上传
  const handleFileUpload = async ({ file, onSuccess, onError, onProgress, fieldKey, mediaType, useTempMedia = true, stepType = 'MESSAGE' }) => {
    try {
      // 更新上传状态
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: true, [fieldKey]: true }));
      setUploadProgress(prev => ({ ...prev, [uploadKey]: 0, [fieldKey]: 0 }));

      // 显示上传进度
      const updateProgress = (percent) => {
        setUploadProgress(prev => ({ ...prev, [uploadKey]: percent, [fieldKey]: percent }));
        if (onProgress) {
          onProgress({ percent });
        }
      };

      // 从localStorage获取租户信息
      const tenantStr = localStorage.getItem('tenant');
      if (!tenantStr) {
        message.error('未获取到租户信息，请重新登录');
        if (onError) onError(new Error('未获取到租户信息'));
        return;
      }
      const tenant = JSON.parse(tenantStr);
      const tenantId = tenant.id;

      // 使用临时素材上传
      const result = await fileService.uploadMedia(file, mediaType, tenantId, updateProgress);

      if (result.success && result.mediaId) {
        console.log(`媒体上传成功 - 字段: ${fieldKey}, mediaId: ${result.mediaId}, ossUrl: ${result.ossUrl}`);

        // 添加预览URL到预览对象
        setPreviewUrls(prev => ({
          ...prev,
          [result.mediaId]: result.ossUrl
        }));

        message.success(`${file.name} 上传成功`);

        if (onSuccess) {
          onSuccess(result);
        }

        return result;
      } else {
        const errorMsg = result?.message || '上传失败';
        message.error(errorMsg);
        if (onError) onError(new Error(errorMsg));
      }
    } catch (error) {
      console.error('文件上传失败:', error);
      message.error(`${file.name} 上传失败: ${error.message}`);
      if (onError) onError(error);
    } finally {
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: false, [fieldKey]: false }));
    }
  };

  // 处理文件移除
  const handleRemoveFile = (fieldKey, stepType = 'MESSAGE') => {
    console.log(`移除文件 - 字段: ${fieldKey}, 步骤类型: ${stepType}`);
    // 清除预览URL
    const attachmentToUpdate = attachments.find(att => att.id === fieldKey);
    if (attachmentToUpdate && attachmentToUpdate.mediaId) {
      setPreviewUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[attachmentToUpdate.mediaId];
        return newUrls;
      });
    }
  };

  // 添加附件
  const handleAddAttachment = (type) => {
    // 生成并返回稳定的ID，供调用方在异步场景下引用
    const id = `attachment_${nextAttachmentId}`;
    const newAttachment = {
      id,
      type: type,
      content: '',
      // 根据类型初始化相关字段
      ...(type === 'IMAGE_GROUP' && { imageGroup: { mediaIds: [], ossUrls: {} } }),
      ...(type === 'VIDEO' && { mediaId: '', ossUrl: '' }),
      ...(type === 'LINK' && { title: '', description: '', url: '' }),
      ...(type === 'MINIPROGRAM' && { title: '', appid: '', pagepath: '', thumbMediaId: '', thumbMediaId_ossUrl: '' }),
      ...(type === 'FILE' && { mediaId: '', ossUrl: '' })
    };

    setAttachments(prev => [...prev, newAttachment]);
    setNextAttachmentId(prev => prev + 1);
    return id;
  };

  // 移除附件
  const handleRemoveAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));

    // 清理相关的预览URL
    const attachmentToRemove = attachments.find(att => att.id === attachmentId);
    if (attachmentToRemove) {
      if (attachmentToRemove.mediaId) {
        setPreviewUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[attachmentToRemove.mediaId];
          return newUrls;
        });
      }
      if (attachmentToRemove.imageGroup && attachmentToRemove.imageGroup.mediaIds) {
        setPreviewUrls(prev => {
          const newUrls = { ...prev };
          attachmentToRemove.imageGroup.mediaIds.forEach(mediaId => {
            delete newUrls[mediaId];
          });
          return newUrls;
        });
      }
    }
  };

  // 更新附件内容
  const handleUpdateAttachmentContent = (attachmentId, field, value) => {
    setAttachments(prev => prev.map(att =>
      att.id === attachmentId ? { ...att, [field]: value } : att
    ));

    // 不在这里直接调用updatePreview，让useEffect处理
  };

  // 插入客户昵称
  const insertNickname = () => {
    setAttachments(prev => {
      const list = Array.isArray(prev) ? prev.slice() : [];
      const idx = list.findIndex(a => a && a.type === 'TEXT');
      if (idx === -1) return [{ id: `text-${Date.now()}`, type: 'TEXT', content: '%NICKNAME%' }, ...list];
      const target = list[idx];
      const before = target.content || '';
      list[idx] = { ...target, content: `${before}%NICKNAME%` };
      return list;
    });
  };

  // 更新预览
  const updatePreview = () => {
    const textAttachment = attachments.find(att => att.type === 'TEXT');
    const otherAttachments = attachments.filter(att => att.type !== 'TEXT');

    console.log('updatePreview - attachments:', attachments);
    console.log('updatePreview - textAttachment:', textAttachment);
    console.log('updatePreview - otherAttachments:', otherAttachments);

    // 构建符合MessagePreview期望格式的数据
    const attachmentList = [];

    // 先添加文本（如果有的话）
    if (textAttachment?.content) {
      attachmentList.push({
        msgtype: 'text',
        text: {
          content: textAttachment.content
        }
      });
    }

    // 再添加其他附件
    otherAttachments.forEach(att => {
      if (att.type === 'IMAGE_GROUP' && att.imageGroup && att.imageGroup.mediaIds && att.imageGroup.mediaIds.length > 0) {
        // 为图片组中的每张图片创建单独的图片附件
        att.imageGroup.mediaIds.forEach(mediaId => {
          attachmentList.push({
            msgtype: 'image',
            image: {
              media_id: mediaId,
              oss_url: att.imageGroup.ossUrls[mediaId] || ''
            }
          });
        });
      } else if (att.type === 'VIDEO' && att.mediaId) {
        attachmentList.push({
          msgtype: 'video',
          video: {
            media_id: att.mediaId,
            oss_url: att.ossUrl || ''
          }
        });
      } else if (att.type === 'LINK' && att.url) {
        attachmentList.push({
          msgtype: 'link',
          link: {
            title: att.title || '',
            desc: att.description || '',
            url: att.url,
            picurl: '',
            pic_oss_url: ''
          }
        });
      } else if (att.type === 'MINIPROGRAM' && att.appid && att.title) {
        attachmentList.push({
          msgtype: 'miniprogram',
          miniprogram: {
            title: att.title,
            appid: att.appid,
            page: att.pagepath || '',
            pic_media_id: att.thumbMediaId || '',
            pic_oss_url: att.thumbMediaId_ossUrl || ''
          }
        });
      } else if (att.type === 'FILE' && att.mediaId) {
        attachmentList.push({
          msgtype: 'file',
          file: {
            media_id: att.mediaId,
            oss_url: att.ossUrl || ''
          }
        });
      }
    });

    const step = {
      actionType: 'SEND_MESSAGE',
      actionParams: {
        text: textAttachment?.content || '',
        attachments: attachmentList
      }
    };

    console.log('updatePreview - 生成的step:', step);
    setPreviewStep(step);
  };

  // 监听附件变化以更新预览
  useEffect(() => {
    updatePreview();
  }, [attachments]);

  // 创建/更新活码
  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      // 构建欢迎语数据
      const textAttachment = attachments.find(att => att.type === 'TEXT');
      const otherAttachments = attachments.filter(att => att.type !== 'TEXT');

      const welcomeData = {
        text: textAttachment?.content || '',
        attachments: otherAttachments.map(att => {
          if (att.type === 'IMAGE_GROUP') {
            return {
              type: 'IMAGE_GROUP',
              imageGroup: att.imageGroup
            };
          } else if (att.type === 'VIDEO') {
            return {
              type: 'VIDEO',
              mediaId: att.mediaId,
              ossUrl: att.ossUrl
            };
          } else if (att.type === 'LINK') {
            return {
              type: 'LINK',
              title: att.title,
              description: att.description,
              url: att.url
            };
          } else if (att.type === 'MINIPROGRAM') {
            return {
              type: 'MINIPROGRAM',
              title: att.title,
              appid: att.appid,
              pagepath: att.pagepath,
              thumbMediaId: att.thumbMediaId,
              thumbMediaId_ossUrl: att.thumbMediaId_ossUrl
            };
          } else if (att.type === 'FILE') {
            return {
              type: 'FILE',
              mediaId: att.mediaId,
              ossUrl: att.ossUrl
            };
          }
          return att;
        }).filter(att => {
          // 过滤掉空的附件
          if (att.type === 'IMAGE_GROUP') {
            return att.imageGroup && att.imageGroup.mediaIds && att.imageGroup.mediaIds.length > 0;
          } else if (att.type === 'VIDEO' || att.type === 'FILE') {
            return att.mediaId;
          } else if (att.type === 'LINK') {
            return att.url;
          } else if (att.type === 'MINIPROGRAM') {
            return att.appid && att.title;
          }
          return false;
        })
      };

      if (isEditMode) {
        // 构建编辑请求数据
        const requestData = {
          remark: values.remark,
          userIds: values.userIds || [],
          skipVerify: values.skipVerify !== undefined ? values.skipVerify : false,
          state: contactWay?.state || `contact_way_${Date.now()}`,
          welcomeMsg: welcomeData,
          autoTags: (values.autoTags || []).map(id => (typeof id === 'number' ? id : parseInt(id, 10)))
        };

        console.log('📤 提交的请求数据(编辑):', requestData);
        const data = await weworkContactWayService.updateContactWay(id, requestData);
        if (data.success) {
          message.success('活码更新成功');
          navigate('/wework-contact-way');
        } else {
          message.error(data.message || '更新失败');
        }
      } else {
        // 创建模式：构建创建请求数据（不设置state，让后端自动生成）
        const requestData = {
          remark: values.remark,
          userIds: values.userIds || [],
          skipVerify: values.skipVerify !== undefined ? values.skipVerify : false,
          welcomeMsg: welcomeData,
          autoTags: (values.autoTags || []).map(id => (typeof id === 'number' ? id : parseInt(id, 10)))
        };

        console.log('📤 提交的请求数据(创建):', requestData);
        const data = await weworkContactWayService.createContactWay(requestData);
        if (data.success) {
          message.success('活码创建成功');
          navigate('/wework-contact-way');
        } else {
          message.error(data.message || '创建失败');
        }
      }
    } catch (error) {
      console.error('更新失败:', error);
      message.error('更新失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="wework-contact-way-page">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" tip={isEditMode ? '加载活码信息中...' : '加载中...'} />
        </div>
      </div>
    );
  }

  return (
    <div className="wework-contact-way-page">
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
            <Breadcrumb>
                <Breadcrumb.Item onClick={() => navigate('/wework-contact-way')}>
                    <Title level={5} style={{ cursor: 'pointer', color: '#1677ff', margin: 0 }}>成员活码</Title>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Title level={5} style={{ margin: 0 }}>{isEditMode ? '编辑活码' : '创建活码'}</Title>
                </Breadcrumb.Item>
            </Breadcrumb>
        </Col>
      </Row>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="contact-way-form"
          initialValues={{
            skipVerify: true,
            autoTags: []
          }}
        >
          <Title level={5} style={{ marginBottom: '24px' }}>基础信息</Title>
          
          {/* 成员活码名称 */}
          <Form.Item
            name="remark"
            label="活码名称"
            rules={[{ required: true, message: '请输入活码名称' }]}
          >
            <Input
              placeholder="请输入活码名称"
              maxLength={50}
              showCount
              style={{ width: '40%' }}
            />
          </Form.Item>

          {/* 选择使用成员 */}
          <Form.Item
            name="userIds"
            label="使用成员"
            rules={[{ required: true, message: '请至少选择一个成员' }]}
          >
            <EmployeeSelect
              multiple
              placeholder="请选择要使用的企业微信成员（可多选）"
              style={{ width: '40%' }}
            />
          </Form.Item>

          {/* 客户添加时无需确认自动成为好友 */}
          <Form.Item
            name="skipVerify"
            label="自动通过好友"
            valuePropName="checked"
            help="开启后，客户通过二维码添加企业成员时，无需等待确认即可成功添加。"
          >
            <Switch
              checkedChildren="开启"
              unCheckedChildren="关闭"
            />
          </Form.Item>

          <Divider />

          {/* 欢迎语设置 */}
          <Title level={5} style={{ marginBottom: '24px' }}>欢迎语设置</Title>
          <Row gutter={24}>
            <Col span={14}>
              <div style={{ marginBottom: '12px' }}>
                <Text strong>编辑欢迎语内容</Text>
                <Button
                  size="small"
                  onClick={insertNickname}
                  style={{ marginLeft: '12px', fontSize: '12px', padding: '0 8px', height: '24px' }}
                >
                  插入昵称
                </Button>
              </div>
              <AttachmentManager
                attachments={attachments}
                onUpdateContent={handleUpdateAttachmentContent}
                onAddAttachment={handleAddAttachment}
                onRemoveAttachment={handleRemoveAttachment}
                previewUrls={previewUrls}
                uploading={uploading}
                uploadProgress={uploadProgress}
                handleFileUpload={handleFileUpload}
                handleRemoveFile={handleRemoveFile}
                stepType="MESSAGE"
                isTemplateActive={false}
              />
            </Col>

            <Col span={10}>
              <div style={{ marginTop: '8px' }}>
                {previewStep ? (
                  <MessagePreview
                    key={JSON.stringify(previewStep)}
                    step={{
                      type: 'MESSAGE',
                      actionType: 'SEND_MESSAGE',
                      messageList: attachments.map(attachment => {
                        if (attachment.type === 'TEXT') {
                          return {
                            type: 'TEXT',
                            params: { content: attachment.content || '' }
                          };
                        } else if (attachment.type === 'IMAGE_GROUP') {
                          return {
                            type: 'IMAGE_GROUP',
                            params: {
                              imageGroup: attachment.imageGroup || { mediaIds: [], ossUrls: {} }
                            }
                          };
                        } else if (attachment.type === 'VIDEO') {
                          return {
                            type: 'VIDEO',
                            params: {
                              mediaId: attachment.mediaId || '',
                              ossUrl: attachment.ossUrl || ''
                            }
                          };
                        } else if (attachment.type === 'LINK') {
                          return {
                            type: 'LINK',
                            params: {
                              title: attachment.title || '',
                              url: attachment.url || '',
                              description: attachment.description || '',
                              picUrl: attachment.picUrl || '',
                              picUrl_ossUrl: attachment.picUrl_ossUrl || ''
                            }
                          };
                        } else if (attachment.type === 'MINIPROGRAM') {
                          return {
                            type: 'MINIPROGRAM',
                            params: {
                              title: attachment.title || '',
                              appid: attachment.appid || '',
                              pagepath: attachment.pagepath || '',
                              thumbMediaId: attachment.thumbMediaId || '',
                              thumbMediaId_ossUrl: attachment.thumbMediaId_ossUrl || ''
                            }
                          };
                        } else if (attachment.type === 'FILE') {
                          return {
                            type: 'FILE',
                            params: {
                              mediaId: attachment.mediaId || '',
                              ossUrl: attachment.ossUrl || ''
                            }
                          };
                        }
                        return null;
                      }).filter(Boolean)
                    }}
                    noTruncate={true}
                  />
                ) : (
                  <div style={{ 
                    padding: '40px', 
                    textAlign: 'center', 
                    color: '#999',
                    border: '1px dashed #d9d9d9',
                    borderRadius: '6px'
                  }}>
                    <Text type="secondary">请编辑欢迎语内容以查看预览效果</Text>
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <Divider />

          {/* 好友打标签 */}
          <Title level={5} style={{ marginBottom: '24px' }}>自动打标签</Title>
          <Form.Item
            name="autoTags"
            label="自动添加标签"
            help="客户成功添加企业成员为好友后，会自动为该客户添加选中的标签。"
          >
            <TagSelect
              mode="multiple"
              placeholder="选择要自动添加的标签（可多选）"
              width={'40%'}
              maxTagCount={8}
            />
          </Form.Item>
        </Form>
      </Card>

      {/* 为固定按钮栏预留空间 */}
      <div style={placeholderStyle}></div>

      <div style={fixedFooterStyle}>
        <Space>
          <Button size="middle" onClick={() => navigate('/wework-contact-way')}>
            取消
          </Button>
          <Button type="primary" onClick={() => form.submit()} loading={saving} icon={<SaveOutlined />} size="middle">
            {isEditMode ? '保存更新' : '创建活码'}
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default WeworkContactWayEdit; 
